import React from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { createThemedStyles } from '@/hooks/use-theme-colors';

export type TimelineTone = 'urgent' | 'hot' | 'warn' | 'default';

const useDotStyle = createThemedStyles<Record<TimelineTone, { bg: string; border: string }>>(
  (th) => ({
    urgent: { bg: '#DC2626', border: '#DC2626' },
    hot: { bg: '#FD5001', border: '#FD5001' },
    warn: { bg: th('#B45309'), border: th('#B45309') },
    default: { bg: th('#fff'), border: th('#D4D4D4') },
  }),
);

export interface TimelineEvent {
  key: string;
  /** Rótulo do dia, ex.: "Quinta, 21 set" ou "Atrasado" / "Aguardando". */
  day: string;
  /** "10:30" — omitido quando o item não tem horário fixo. */
  hour?: string;
  tone: TimelineTone;
  content: React.ReactNode;
}

/**
 * Feed único, em ordem de prioridade cronológica: cobrança atrasada primeiro,
 * depois o próximo horário, pedidos pendentes, o resto da agenda e o que vem
 * a vencer. É o que diferencia a home "linha do tempo" de uma lista comum —
 * tudo que importa hoje mora num só lugar, um embaixo do outro.
 */
export function TimelineFeed({ events }: { events: TimelineEvent[] }) {
  const DOT_STYLE = useDotStyle();
  return (
    <View>
      {events.map((event, index) => {
        const dot = DOT_STYLE[event.tone];
        const isLast = index === events.length - 1;
        return (
          <View key={event.key} className="flex-row">
            <View className="w-6 items-center">
              <View
                className="mt-1 h-2.5 w-2.5 rounded-full border-2"
                style={{ backgroundColor: dot.bg, borderColor: dot.border }}
              />
              {!isLast && <View className="mt-1 w-0.5 flex-1 bg-neutral-200" />}
            </View>
            <View className="flex-1 gap-2 pb-5 pl-3">
              <ThemedText className="font-bold text-[11px] uppercase tracking-wide text-neutral-400">
                {event.day}
                {event.hour ? ` · ${event.hour}` : ''}
              </ThemedText>
              {event.content}
            </View>
          </View>
        );
      })}
    </View>
  );
}
